import { Check, CirclePlay, MessageCircle, MessageCircleQuestion } from "lucide-react";

/**
 * Visual marker for "what kind of session is this" — chat (a hand-typed
 * conversation) vs routine (one fire of a scheduled / webhook routine).
 *
 * State priority (highest first):
 *   1. `running` — agent thread is live right now (streaming in background)
 *   2. `awaiting` — parked on a HITL tool call, needs user reply
 *   3. kind — chat or routine
 *
 * `variant="badge"` switches to a larger circular pill (32px, themed bg)
 * used in hero/recent lists; default renders the inline 16px glyph used
 * by sidebar history and dense rows.
 */

interface Props {
  kind: "chat" | "routine";
  className?: string;
  awaiting?: boolean;
  running?: boolean;
  variant?: "default" | "badge";
}

function AwaitingExclamation() {
  return (
    <svg viewBox="0 0 32 32" width="20" height="20" fill="none" aria-hidden="true">
      <rect x="14.4" y="8" width="3.2" height="12" rx="1.6" fill="currentColor" />
      <circle cx="16" cy="23.2" r="1.6" fill="currentColor" />
    </svg>
  );
}

export function SessionTypeIcon({ kind, className, awaiting, running, variant = "default" }: Props) {
  const state = running ? "running" : awaiting ? "awaiting" : kind;
  const cls = `session-type-icon session-type-icon--${variant} session-type-icon--${state}${className ? ` ${className}` : ""}`;

  if (running) {
    return (
      <span className={cls} aria-label="Выполняется сейчас" title="Выполняется сейчас">
        <CirclePlay size={variant === "badge" ? 20 : 16} strokeWidth={1.75} aria-hidden="true" />
      </span>
    );
  }
  if (awaiting) {
    return (
      <span className={cls} aria-label="Требуется ваш ответ" title="Требуется ваш ответ">
        {variant === "badge" ? (
          <AwaitingExclamation />
        ) : (
          <MessageCircleQuestion size={16} strokeWidth={1.75} aria-hidden="true" />
        )}
      </span>
    );
  }
  if (kind === "routine") {
    return (
      <span className={cls} aria-label="Запуск автоматизации" title="Запуск автоматизации">
        <Check size={16} strokeWidth={2.25} aria-hidden="true" />
      </span>
    );
  }
  return (
    <span className={cls} aria-label="Чат" title="Чат">
      <MessageCircle size={16} strokeWidth={1.75} aria-hidden="true" />
    </span>
  );
}
